/**
 * Beu Messages: the shapes the screens work with, and the pure helpers that
 * turn them into what is drawn. Nothing in here talks to a server or touches
 * the DOM, so it is all unit tested (model.test.js).
 *
 * Person        { id, name, role, status: { state, until } }
 *                 state: 'free' | 'client' | 'break' | 'off'
 * Conversation  { id, space, kind, name, topic, memberIds, pinned, muted,
 *                 createdAt, lastMessageAt, readUpTo: { [userId]: ISO } }
 *                 space: 'team' | 'clients' | 'support'
 *                 kind:  'channel' | 'dm' | 'group'
 * Message       { id, conversationId, authorId, createdAt, text,
 *                 attachments: [{ type, ... }], card, replyToId, threadRootId,
 *                 reactions: { [emoji]: [userId] }, mentions: [userId],
 *                 editedAt, deletedAt, status, system }
 *                 status (own messages only): 'sending' | 'sent' | 'failed'
 *                 system: { text } for "Leah joined" style lines
 */

/** The app's avatar palette (catalogue-data/primitives.js), picked by id. */
export const AVATAR_COLOURS = ['#007EA0', '#6366F1', '#0EA5A4', '#D9921A', '#DB6FB0', '#5B8DEF', '#16A34A', '#E5484D'];

/** Messages from one person closer together than this join one stack. */
export const STACK_MINUTES = 3;

/** How long after sending a message can still be edited or unsent. */
export const EDIT_WINDOW_MINUTES = 15;

/** The six reactions on the quick bar, in order. */
export const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '🙏', '🔥'];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const toDate = (v) => (v instanceof Date ? v : new Date(v));
const pad = (n) => (n < 10 ? `0${n}` : String(n));
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const dayDiff = (a, b) => Math.round((startOfDay(a) - startOfDay(b)) / 86400000);

export function initialsOf(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function colourFor(id) {
    const s = String(id || '');
    let h = 0;
    for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 2147483647;
    return AVATAR_COLOURS[h % AVATAR_COLOURS.length];
}

export function firstName(name) {
    return String(name || '').trim().split(/\s+/)[0] || '';
}

/** 09:05 */
export function formatTime(value) {
    const d = toDate(value);
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The sticky label between days: Today, Yesterday, Mon 6 Oct, 6 Oct 2025. */
export function dayLabel(value, now = new Date()) {
    const d = toDate(value);
    const diff = dayDiff(now, d);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (d.getFullYear() !== now.getFullYear()) return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
    return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** The time on an inbox row: 09:05, Yesterday, Mon, 6 Oct. */
export function listTime(value, now = new Date()) {
    const d = toDate(value);
    const diff = dayDiff(now, d);
    if (diff === 0) return formatTime(d);
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return DAYS[d.getDay()];
    return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "until 15:30" style status words; null when someone is free. */
export function statusLine(status) {
    if (!status || !status.state || status.state === 'free') return null;
    const until = status.until ? ` until ${formatTime(status.until)}` : '';
    if (status.state === 'client') return `With a client${until}`;
    if (status.state === 'break') return `On a break${until}`;
    if (status.state === 'off') return 'Off today';
    return null;
}

/**
 * Lay a conversation out for drawing: day dividers between days, and each
 * message told whether it starts or ends a stack (same person, a few minutes
 * apart, no day change, no system line between).
 */
export function layoutMessages(messages, now = new Date()) {
    const rows = [];
    let prev = null;
    let lastDay = null;
    messages.forEach((m, i) => {
        const at = toDate(m.createdAt);
        const day = startOfDay(at).getTime();
        if (day !== lastDay) {
            rows.push({ type: 'day', key: `day-${day}`, label: dayLabel(at, now) });
            lastDay = day;
            prev = null;
        }
        if (m.system || m.auto || m.call) {
            rows.push({ type: m.call ? 'call' : m.auto ? 'auto' : 'system', key: m.id, message: m });
            prev = null;
            return;
        }
        const next = messages[i + 1];
        // A run of bubbles breaks when the channel changes (a text, then an email)
        const joinsPrev = !!prev && prev.authorId === m.authorId && !!prev.note === !!m.note && (prev.channel || null) === (m.channel || null)
            && (at - toDate(prev.createdAt)) / 60000 < STACK_MINUTES;
        const nextAt = next ? toDate(next.createdAt) : null;
        const joinsNext = !!next && !next.system && !next.auto && !next.call && next.authorId === m.authorId && !!next.note === !!m.note && (next.channel || null) === (m.channel || null)
            && startOfDay(nextAt).getTime() === day
            && (nextAt - at) / 60000 < STACK_MINUTES;
        rows.push({ type: 'message', key: m.id, message: m, first: !joinsPrev, last: !joinsNext });
        prev = m;
    });
    return rows;
}

/** Messages in the main timeline: thread replies live in their thread. */
export function mainTimeline(messages) {
    return messages.filter((m) => !m.threadRootId);
}

export function threadReplies(messages, rootId) {
    return messages.filter((m) => m.threadRootId === rootId);
}

/** Unread = other people's messages after my read marker. */
export function unreadCount(conversation, messages, meId) {
    const mark = conversation && conversation.readUpTo && conversation.readUpTo[meId];
    const since = mark ? toDate(mark).getTime() : 0;
    return mainTimeline(messages).filter((m) => m.authorId !== meId && !m.system && !m.auto && !m.deletedAt
        && toDate(m.createdAt).getTime() > since).length;
}

/** The first unread message id, for the "New messages" line. */
export function firstUnreadId(conversation, messages, meId) {
    const mark = conversation && conversation.readUpTo && conversation.readUpTo[meId];
    const since = mark ? toDate(mark).getTime() : 0;
    const m = mainTimeline(messages).find((x) => x.authorId !== meId && !x.system && !x.auto
        && toDate(x.createdAt).getTime() > since);
    return m ? m.id : null;
}

/** One line for the inbox: "You: Sounds good", "Photo", "Booking: Fri 10:30". */
export function previewText(message, people, meId) {
    if (!message) return '';
    if (message.system) return message.system.text;
    if (message.auto) return `${message.auto.label} sent by ${message.auto.channel.toLowerCase()}`;
    if (message.call) return `Phone call: ${message.call.outcome || callLength(message.call.seconds)}`;
    if (message.deletedAt) return 'Message removed';
    if (message.note) return `Note: ${(message.text || '').replace(/\s+/g, ' ').trim()}`;
    let body = (message.text || '').replace(/\s+/g, ' ').trim();
    if (!body && message.card) body = cardLabel(message.card);
    if (!body && message.attachments && message.attachments.length) body = attachmentLabel(message.attachments[0]);
    if (message.authorId === meId) return `You: ${body}`;
    const author = people && people[message.authorId];
    return author && message.conversationKind !== 'dm' ? `${firstName(author.name)}: ${body}` : body;
}

export function attachmentLabel(a) {
    if (!a) return '';
    if (a.type === 'image') return 'Photo';
    if (a.type === 'gif') return 'GIF';
    if (a.type === 'sticker') return 'Sticker';
    if (a.type === 'voice') return 'Voice note';
    if (a.type === 'file') return a.name ? `File: ${a.name}` : 'File';
    return a.name || 'File';
}

export function cardLabel(card) {
    if (!card) return '';
    if (card.type === 'booking') return `Booking: ${card.when || ''}`.trim();
    if (card.type === 'client') return `Client: ${card.name || ''}`.trim();
    if (card.type === 'slots') return card.picked ? `Booked ${card.picked}` : `Free times for ${card.service}`;
    if (card.type === 'deposit') return card.status === 'paid' ? `Deposit paid, £${card.amount}` : `Deposit request, £${card.amount}`;
    if (card.type === 'form') return `Form: ${card.name}`;
    if (card.type === 'cover') return `Cover: ${(card.booking && card.booking.when) || 'a booking'}`;
    if (card.type === 'post') return `Post: ${card.title}`;
    if (card.type === 'campaign') return `Campaign: ${card.title}`;
    if (card.type === 'email') return `Email: ${card.title}`;
    if (card.type === 'ticket') return `Ticket ${card.ref}: ${card.title}`;
    if (card.type === 'ticket-fixed') return `Fixed: ${card.title}`;
    return 'Shared from Beu';
}

/** Can I still edit or unsend this? Own, not removed, inside the window. */
export function canChange(message, meId, now = new Date()) {
    if (!message || message.authorId !== meId || message.deletedAt || message.system) return false;
    if (message.status === 'sending' || message.status === 'failed') return false;
    // Scheduled and not gone yet: still yours to change or call off.
    if (message.deliverAt && toDate(message.deliverAt) > toDate(now)) return true;
    return (toDate(now) - toDate(message.createdAt)) / 60000 <= EDIT_WINDOW_MINUTES;
}

/** Add my reaction, or take it away if it is already mine. Immutable. */
export function toggleReaction(reactions, emoji, userId) {
    const next = { ...(reactions || {}) };
    const who = next[emoji] || [];
    if (who.indexOf(userId) !== -1) {
        const left = who.filter((id) => id !== userId);
        if (left.length) next[emoji] = left; else delete next[emoji];
    } else {
        next[emoji] = who.concat(userId);
    }
    return next;
}

/** Reactions as chips, most used first, mine marked. */
export function reactionChips(reactions, meId) {
    return Object.keys(reactions || {})
        .map((emoji) => ({ emoji, count: reactions[emoji].length, mine: reactions[emoji].indexOf(meId) !== -1, who: reactions[emoji] }))
        .filter((c) => c.count > 0)
        .sort((a, b) => b.count - a.count);
}

/**
 * Who an @mention points at: "@Jade" or "@Jade Miller" against the people
 * in the conversation. Returns their ids, once each.
 */
export function findMentions(text, people) {
    const out = [];
    // The team and Bailey can be @mentioned; never clients or the Beu team
    const list = Object.keys(people || {}).map((id) => people[id]).filter((p) => p && p.kind !== 'client' && p.kind !== 'beu');
    const re = /@([A-Za-z][A-Za-z'-]*(?: [A-Za-z][A-Za-z'-]*)?)/g;
    let m = re.exec(text || '');
    while (m) {
        const said = m[1].toLowerCase();
        const hit = list.find((p) => p.name.toLowerCase() === said)
            || list.find((p) => firstName(p.name).toLowerCase() === said.split(' ')[0]);
        if (hit && out.indexOf(hit.id) === -1) out.push(hit.id);
        m = re.exec(text || '');
    }
    return out;
}

/** The @word being typed at the caret, for the mention picker; null if none. */
export function mentionQuery(text, caret) {
    const upto = String(text || '').slice(0, caret);
    const at = upto.lastIndexOf('@');
    if (at === -1) return null;
    if (at > 0 && !/\s/.test(upto[at - 1])) return null;
    const q = upto.slice(at + 1);
    if (/\s/.test(q) || q.length > 20) return null;
    return { start: at, query: q };
}

/** A message that is only emoji (1 to 3) is drawn large with no bubble. */
export function isJumboEmoji(text) {
    const t = String(text || '').trim();
    if (!t || t.length > 24) return false;
    if (/[A-Za-z0-9]/.test(t)) return false;
    const chars = Array.from(t.replace(/\s+/g, '')).filter((c) => !/[‍️\u{1F3FB}-\u{1F3FF}]/u.test(c));
    return chars.length >= 1 && chars.length <= 3 && /\p{Extended_Pictographic}/u.test(t);
}

/** Sort an inbox: pinned first, then newest activity. */
export function sortConversations(list) {
    return list.slice().sort((a, b) => {
        if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
        return toDate(b.lastMessageAt || b.createdAt) - toDate(a.lastMessageAt || a.createdAt);
    });
}

/** The name to show for a conversation from where I sit. */
export function conversationTitle(conversation, people, meId) {
    if (!conversation) return '';
    if (conversation.kind === 'channel') return conversation.name;
    if (conversation.kind === 'client') return (people[conversation.clientId] || {}).name || 'Client';
    if (conversation.name) return conversation.name;
    const others = (conversation.memberIds || []).filter((id) => id !== meId).map((id) => people[id]).filter(Boolean);
    if (conversation.kind === 'dm') return others[0] ? others[0].name : 'Direct message';
    return others.map((p) => firstName(p.name)).join(', ');
}

/**
 * A channel name as people type it, made tidy: "Colour Bar!" → "colour-bar".
 * Lower case, letters, numbers and single hyphens, up to 40 characters.
 */
export function channelName(text) {
    return String(text || '').toLowerCase().replace(/^#+/, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40).replace(/-+$/, '');
}

/**
 * Channels in my order: any I haven't placed yet (new ones) first, newest
 * first as usual, then the ones I've arranged, in my order.
 */
export function orderChannels(rows, order) {
    if (!order || !order.length) return rows;
    const at = {};
    order.forEach((id, i) => { at[id] = i; });
    const fresh = rows.filter((r) => !(r.conversation.id in at));
    const placed = rows.filter((r) => r.conversation.id in at).sort((a, b) => at[a.conversation.id] - at[b.conversation.id]);
    return fresh.concat(placed);
}

/** A quick reply ready to send: {first name} becomes the client's, {my name} mine. */
export function fillReply(text, clientName, myName) {
    return String(text || '')
        .replace(/\{first name\}/gi, firstName(clientName) || 'there')
        .replace(/\{my name\}/gi, firstName(myName) || '');
}

/** "/park" at the start of the box: the quick replies whose title or text match. */
export function matchReplies(replies, query) {
    const q = String(query || '').trim().toLowerCase();
    const list = replies || [];
    if (!q) return list;
    // A name that matches beats words in the message ("/aft" finds Aftercare before Parking)
    const byTitle = list.filter((r) => r.title.toLowerCase().indexOf(q) !== -1);
    return byTitle.concat(list.filter((r) => byTitle.indexOf(r) === -1 && r.text.toLowerCase().indexOf(q) !== -1));
}

/**
 * Messages in a space whose words match (two letters or more): newest
 * first, at most `limit`. Each hit: { conversation, message, before, match, after }
 * with the words around the match, for a one-line snippet.
 */
export function searchMessages({ conversations, messages, space, query, meId, limit = 30 }) {
    const q = String(query || '').trim().toLowerCase();
    if (q.length < 2) return [];
    const hits = [];
    Object.keys(conversations).forEach((id) => {
        const c = conversations[id];
        if (c.space !== space || (c.memberIds && meId && c.memberIds.indexOf(meId) === -1)) return;
        (messages[id] || []).forEach((m) => {
            if (m.system || m.auto || m.deletedAt || !m.text) return;
            const at = m.text.toLowerCase().indexOf(q);
            if (at === -1) return;
            const start = Math.max(0, at - 28);
            hits.push({
                conversation: c, message: m,
                before: (start > 0 ? '…' : '') + m.text.slice(start, at),
                match: m.text.slice(at, at + q.length),
                after: m.text.slice(at + q.length, at + q.length + 60),
            });
        });
    });
    return hits.sort((a, b) => (a.message.createdAt < b.message.createdAt ? 1 : -1)).slice(0, limit);
}

const URL_RE = /(https?:\/\/[^\s<]+|www\.[^\s<]+\.[^\s<]+)/i;
const SITES = {
    'instagram.com': 'Instagram', 'tiktok.com': 'TikTok', 'facebook.com': 'Facebook', 'youtube.com': 'YouTube', 'youtu.be': 'YouTube',
    'google.com': 'Google', 'maps.google.com': 'Google Maps', 'goo.gl': 'Google Maps', 'pinterest.com': 'Pinterest', 'pin.it': 'Pinterest',
    'beuapp.com': 'Beu', 'beu.app': 'Beu',
};

/** The first web address in some words (trailing punctuation left off), or null. */
export function firstUrl(text) {
    const m = URL_RE.exec(String(text || ''));
    if (!m) return null;
    const raw = m[1].replace(/[.,!?)\]'"]+$/, '');
    return raw.indexOf('http') === 0 ? raw : `https://${raw}`;
}

/**
 * A preview for a link, from its address alone: the site's name and a title
 * from the last part of the path ("/p/balayage-ideas" → "Balayage ideas").
 * The real backend fetches the page's own title and image instead.
 */
export function linkPreview(url) {
    try {
        const u = new URL(url);
        const host = u.hostname.replace(/^www\./, '');
        const site = SITES[host] || SITES[host.split('.').slice(-2).join('.')] || host;
        const last = decodeURIComponent(u.pathname.split('/').filter(Boolean).pop() || '');
        const words = last.replace(/\.[a-z0-9]+$/i, '').replace(/[-_+]+/g, ' ').trim();
        const title = words && !/^[A-Za-z0-9]{8,}$/.test(words) ? words.charAt(0).toUpperCase() + words.slice(1) : site;
        return { url: u.href, site, host, title };
    } catch (e) {
        return null;
    }
}

/** "Paused until 15:30" / "Paused until tomorrow 09:00", or null once it's over. */
export function pausedLine(pausedUntil, now = new Date()) {
    if (!pausedUntil) return null;
    const at = toDate(pausedUntil);
    const base = toDate(now);
    if (at <= base) return null;
    const ahead = Math.round((new Date(at.getFullYear(), at.getMonth(), at.getDate()) - new Date(base.getFullYear(), base.getMonth(), base.getDate())) / 86400000);
    return `Paused until ${ahead === 0 ? '' : ahead === 1 ? 'tomorrow ' : `${DAYS[at.getDay()]} `}${formatTime(at)}`;
}

/** The other person in a DM, or null. */
export function dmPartner(conversation, people, meId) {
    if (!conversation || conversation.kind !== 'dm') return null;
    const id = (conversation.memberIds || []).find((x) => x !== meId);
    return (id && people[id]) || null;
}

let seq = 0;
/** Local ids for optimistic messages, swapped for server ids on send. */
export function localId(prefix = 'tmp') {
    seq += 1;
    return `${prefix}-${Date.now().toString(36)}-${seq}`;
}

/* ---------------------------------------------- reminders and deliver later */

/**
 * The quick choices for "Remind me": after my next client (from the diary,
 * when there is one), in an hour, the end of the day, tomorrow morning.
 * Times that have already passed today roll on sensibly.
 */
export function reminderOptions(now = new Date(), nextClientEndsAt = null) {
    const base = toDate(now);
    const out = [];
    if (nextClientEndsAt && toDate(nextClientEndsAt) > base) {
        out.push({ id: 'after-client', label: 'After my next client', sub: `About ${formatTime(nextClientEndsAt)}, from your diary`, dueAt: toDate(nextClientEndsAt).toISOString(), fromDiary: true });
    }
    out.push({ id: 'hour', label: 'In 1 hour', sub: formatTime(new Date(base.getTime() + 3600000)), dueAt: new Date(base.getTime() + 3600000).toISOString() });
    const evening = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 18, 0);
    if (evening > new Date(base.getTime() + 30 * 60000)) {
        out.push({ id: 'evening', label: 'At the end of the day', sub: 'Today at 18:00', dueAt: evening.toISOString() });
    }
    const tomorrow = new Date(base.getFullYear(), base.getMonth(), base.getDate() + 1, 9, 0);
    out.push({ id: 'tomorrow', label: 'Tomorrow morning', sub: `${DAYS[tomorrow.getDay()]} at 09:00`, dueAt: tomorrow.toISOString() });
    return out;
}

/**
 * "Delivers at 15:30" for a message held until someone is free or scheduled
 * for later; "Delivers tomorrow at 09:00" or "Delivers Wed 15 Oct at 09:00"
 * when it's not today. Null once delivered.
 */
export function deliveryLine(message, now = new Date()) {
    if (!message || !message.deliverAt) return null;
    const at = toDate(message.deliverAt);
    if (at <= toDate(now)) return null;
    const base = toDate(now);
    const ahead = Math.round((new Date(at.getFullYear(), at.getMonth(), at.getDate()) - new Date(base.getFullYear(), base.getMonth(), base.getDate())) / 86400000);
    if (ahead === 0) return `Delivers at ${formatTime(at)}`;
    if (ahead === 1) return `Delivers tomorrow at ${formatTime(at)}`;
    return `Delivers ${dayLabel(at, base)} at ${formatTime(at)}`;
}

/** Should a message to this person wait? Only while they are with a client or on a break. */
export function holdUntil(person, now = new Date()) {
    const st = person && person.status;
    if (!st || (st.state !== 'client' && st.state !== 'break') || !st.until) return null;
    return toDate(st.until) > toDate(now) ? st.until : null;
}

/** When a reminder rings: "Today at 15:30", "Tomorrow at 09:00", "Wed 15 Oct at 09:00". */
export function dueLabel(value, now = new Date()) {
    const d = toDate(value);
    const diff = dayDiff(d, now);
    const time = formatTime(d);
    if (diff === 0) return `Today at ${time}`;
    if (diff === 1) return `Tomorrow at ${time}`;
    return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]} at ${time}`;
}

/** The last message worth showing on an inbox row: not a quiet automated line. */
export function lastShown(messages) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
        const m = messages[i];
        if (!m.threadRootId && !m.auto) return m;
    }
    return null;
}

/** Which side a message sits on. In client chats the salon (any team member, or Bailey) is "our" side. */
export function isOutgoing(message, conversation, people, meId) {
    if (!conversation || conversation.space !== 'clients') return message.authorId === meId;
    const author = people[message.authorId];
    return !!author && author.kind !== 'client';
}

/* ------------------------------------------- one client inbox (13 Oct 2026) */

/** Where a client message came from, or how the salon's reply went. */
export const CHANNEL_LABELS = { app: 'Beu app', 'booking-page': 'Booking page', sms: 'Text', email: 'Email', call: 'Phone call' };
/** The icon beside each channel, wherever it's shown (the tag, the time line, the switch line). */
export const CHANNEL_ICONS = { app: 'chat', 'booking-page': 'globe', sms: 'mobile', email: 'mail', call: 'phone' };

export function channelLabel(channel) { return CHANNEL_LABELS[channel] || ''; }

/** How a reply to this client goes: by text or email when they don't have the app. */
export function replyChannel(client) {
    // Function declarations hoist, so this can use the texting helpers further down
    return defaultChannel(client, null) || 'app'; // eslint-disable-line no-use-before-define
}

/**
 * Bailey in a client chat: 'replying' (he answers), 'needs-you' (he handed
 * it to the team and waits) or 'paused' (someone took over). null when
 * Bailey has never been in the chat.
 */
export function baileyState(conversation) {
    return (conversation && conversation.bailey && conversation.bailey.state) || null;
}

export function needsYou(conversation) { return baileyState(conversation) === 'needs-you'; }

export function callLength(seconds) {
    const s = Math.max(0, Math.round(Number(seconds) || 0));
    if (s < 60) return `${s} sec`;
    return `${Math.round(s / 60)} min`;
}

/* ------------------------------------------ texting clients (14 Oct 2026) */

/** Prices when the server doesn't send them: Billing's text and email prices. */
export const DEFAULT_PENCE = { app: 0, sms: 8, email: 2 };

/**
 * How the salon can reach a client, cheapest last: [{ channel, pence, to, ok, blocked }].
 * The server sends reach.channels (backend brief, section 28); older shapes
 * ({ channel: 'text' | 'email' | 'app', to }) still work. A client who replied
 * STOP keeps Text in the list, crossed out.
 */
export function reachChannels(client) {
    const reach = (client && client.reach) || { channel: 'app' };
    const list = reach.channels && reach.channels.length ? reach.channels
        : [{ channel: reach.channel === 'text' ? 'sms' : reach.channel || 'app', to: reach.to, pence: reach.pence }];
    return list.map((c) => {
        const stopped = c.channel === 'sms' && !!reach.textsOff;
        return {
            ...c,
            pence: c.pence != null ? c.pence : DEFAULT_PENCE[c.channel] || 0,
            ok: c.ok !== false && !stopped,
            blocked: stopped ? 'stopped' : c.blocked || null,
        };
    });
}

/** The way to reply: how the client last wrote, if that still works, else app, text, email. */
export function defaultChannel(client, messages) {
    const usable = reachChannels(client).filter((c) => c.ok);
    if (!usable.length) return null;
    const theirs = (messages || []).filter((m) => client && m.authorId === client.id && m.channel).slice(-1)[0];
    const wrote = theirs && usable.find((c) => c.channel === theirs.channel);
    if (wrote) return wrote.channel;
    const order = ['app', 'sms', 'email'];
    return usable.slice().sort((a, b) => order.indexOf(a.channel) - order.indexOf(b.channel))[0].channel;
}

export function formatPence(pence) {
    if (!pence) return 'free';
    return pence < 100 ? `${pence}p` : `£${(pence / 100).toFixed(2)}`;
}

/** Why a text or email didn't go, for the line under the bubble. */
export function failedLine(reason, clientFirst) {
    if (reason === 'stopped') return `Not sent. ${clientFirst || 'They'} turned off texts`;
    if (reason === 'balance-paused') return 'Not sent. Texts are paused on your balance';
    if (reason === 'not-allowed') return "Not sent. Your role can't text or email clients";
    if (reason === 'carrier') return 'Not sent. The phone network refused it';
    return 'Not sent';
}

/** Texts and emails the salon sent to clients this month, for the spend line. */
export function directSpend(conversations, messages, people, now = new Date()) {
    const start = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const out = { texts: 0, emails: 0, pence: 0 };
    Object.keys(conversations).forEach((id) => {
        const c = conversations[id];
        if (c.space !== 'clients') return;
        const client = people[c.clientId];
        const prices = {};
        reachChannels(client).forEach((ch) => { prices[ch.channel] = ch.pence; });
        (messages[id] || []).forEach((m) => {
            if (m.note || m.status === 'failed' || m.status === 'sending' || m.authorId === c.clientId) return;
            if (m.channel !== 'sms' && m.channel !== 'email') return;
            if (toDate(m.createdAt).getTime() < start) return;
            if (m.channel === 'sms') out.texts += 1; else out.emails += 1;
            out.pence += prices[m.channel] != null ? prices[m.channel] : DEFAULT_PENCE[m.channel];
        });
    });
    return out;
}
